export const MIN_PREVIEW_SCALE = 0.1;
export const PREVIEW_ZOOM_FACTOR = 1.15;
export const PREVIEW_HEADER_HEIGHT = 48;
export const PREVIEW_CLOSE_BUTTON_SIZE = 36;
export const PREVIEW_CLOSE_BUTTON_RIGHT = 10;
export const PREVIEW_MIN_VISIBLE_HEADER_WIDTH = 160;

const finiteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const clamp = (value, minimum, maximum) =>
  Math.min(Math.max(value, minimum), maximum);

export const normalizePreviewRotation = (rotation) => {
  const normalized = finiteNumber(rotation, 0) % 360;
  return normalized < 0 ? normalized + 360 : normalized;
};

export const resolvePreviewScale = (
  scale,
  factor,
  minimumScale = MIN_PREVIEW_SCALE
) => {
  const currentScale = Math.max(
    finiteNumber(scale, 1),
    finiteNumber(minimumScale, MIN_PREVIEW_SCALE)
  );
  const zoomFactor = finiteNumber(factor, 1);
  if (zoomFactor <= 0) return currentScale;

  const nextScale = currentScale * zoomFactor;
  if (!Number.isFinite(nextScale)) return currentScale;
  return Math.max(nextScale, minimumScale);
};

export const zoomPreviewAroundPoint = (
  transform,
  { factor, anchorX = 0, anchorY = 0, minimumScale = MIN_PREVIEW_SCALE }
) => {
  const currentScale = Math.max(
    finiteNumber(transform && transform.scale, 1),
    minimumScale
  );
  const nextScale = resolvePreviewScale(
    currentScale,
    factor,
    minimumScale
  );
  const currentOffsetX = finiteNumber(transform && transform.offsetX, 0);
  const currentOffsetY = finiteNumber(transform && transform.offsetY, 0);

  if (nextScale === currentScale) {
    return {
      scale: currentScale,
      offsetX: currentOffsetX,
      offsetY: currentOffsetY,
    };
  }

  const ratio = nextScale / currentScale;
  const focalX = finiteNumber(anchorX, 0);
  const focalY = finiteNumber(anchorY, 0);
  const nextOffsetX = focalX - ratio * (focalX - currentOffsetX);
  const nextOffsetY = focalY - ratio * (focalY - currentOffsetY);

  return {
    scale: nextScale,
    offsetX: finiteNumber(nextOffsetX, currentOffsetX),
    offsetY: finiteNumber(nextOffsetY, currentOffsetY),
  };
};

export const getCenteredPreviewPosition = ({
  viewportWidth,
  viewportHeight,
  dialogWidth,
  dialogHeight,
}) => ({
  x: Math.max(
    0,
    (finiteNumber(viewportWidth, 0) - finiteNumber(dialogWidth, 0)) / 2
  ),
  y: Math.max(
    0,
    (finiteNumber(viewportHeight, 0) - finiteNumber(dialogHeight, 0)) / 2
  ),
});

export const constrainPreviewPosition = (
  position,
  {
    viewportWidth,
    viewportHeight,
    dialogWidth,
    headerHeight = PREVIEW_HEADER_HEIGHT,
    closeButtonSize = PREVIEW_CLOSE_BUTTON_SIZE,
    closeButtonRight = PREVIEW_CLOSE_BUTTON_RIGHT,
    minimumVisibleHeaderWidth = PREVIEW_MIN_VISIBLE_HEADER_WIDTH,
  }
) => {
  const safeViewportWidth = Math.max(finiteNumber(viewportWidth, 0), 0);
  const safeViewportHeight = Math.max(finiteNumber(viewportHeight, 0), 0);
  const safeDialogWidth = Math.max(finiteNumber(dialogWidth, 0), 0);
  const safeHeaderHeight = Math.max(finiteNumber(headerHeight, 0), 0);
  const safeCloseButtonSize = Math.max(
    finiteNumber(closeButtonSize, 0),
    0
  );
  const safeCloseButtonRight = Math.max(
    finiteNumber(closeButtonRight, 0),
    0
  );
  const visibleHeaderWidth = Math.min(
    Math.max(finiteNumber(minimumVisibleHeaderWidth, 0), 0),
    safeDialogWidth,
    safeViewportWidth
  );
  const minimumX = Math.max(
    visibleHeaderWidth - safeDialogWidth,
    safeCloseButtonRight + safeCloseButtonSize - safeDialogWidth
  );
  const maximumX = Math.max(
    minimumX,
    safeViewportWidth - safeDialogWidth + safeCloseButtonRight
  );
  const maximumY = Math.max(0, safeViewportHeight - safeHeaderHeight);

  return {
    x: clamp(finiteNumber(position && position.x, 0), minimumX, maximumX),
    y: clamp(finiteNumber(position && position.y, 0), 0, maximumY),
  };
};
